import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Search, Edit2, Trash2, X, Users as UsersIcon, ShieldCheck, UserCheck, ShieldOff, Loader, Eye } from 'lucide-react';
import { toast } from 'react-toastify';
import { RootState } from '../store/reducers';
import { ApiUser, Role } from '../store/users/reducer';
import {
  fetchUsersRequest,
  createUserRequest,
  updateUserRequest,
  deleteUserRequest,
  fetchRolesRequest,
  suspendUserRequest,
  activateUserRequest,
} from '../store/users/actions';
import './Users.css';

const roleColorMap: Record<string, { color: string; bg: string; label: string }> = {
  admin:       { color: '#7C3AED', bg: '#F5F3FF', label: 'Administrateur' },
  superadmin:  { color: '#8B6914', bg: '#FFF8E7', label: 'Super Admin' },
  agent:       { color: '#2563EB', bg: '#EFF6FF', label: 'Agent' },
  validateur:  { color: '#059669', bg: '#ECFDF5', label: 'Validateur' },
  user:        { color: '#6B7280', bg: '#F3F4F6', label: 'Utilisateur' },
};

const getRoleDisplay = (roles: Array<{ id: number; name: string }>) => {
  if (!roles || roles.length === 0) return { color: '#6B7280', bg: '#F3F4F6', label: 'Aucun rôle' };
  const primary = roles[0].name;
  return roleColorMap[primary] || { color: '#6B7280', bg: '#F3F4F6', label: primary };
};

const Users: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { users, isLoading, count, isSubmitting, roles, rolesLoading } = useSelector(
    (state: RootState) => state.users
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('Tous');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<ApiUser | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showToggleConfirm, setShowToggleConfirm] = useState<string | null>(null);

  // Form state
  const [formFirstName, setFormFirstName] = useState('');
  const [formLastName, setFormLastName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formRoleIds, setFormRoleIds] = useState<number[]>([]);
  const [formPassword, setFormPassword] = useState('');

  useEffect(() => {
    dispatch(fetchUsersRequest());
    dispatch(fetchRolesRequest());
  }, [dispatch]);

  const filtered = users.filter(u => {
    const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
    const matchSearch = searchQuery === '' ||
      fullName.includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchRole = filterRole === 'Tous' ||
      (u.role || []).some(r => r.name === filterRole);
    return matchSearch && matchRole;
  });

  const totalUsers = count || users.length;
  const activeUsers = users.filter(u => u.is_active).length;
  const adminCount = users.filter(u => (u.role || []).some(r => r.name === 'admin' || r.name === 'superadmin')).length;

  const resetForm = () => {
    setFormFirstName('');
    setFormLastName('');
    setFormEmail('');
    setFormPhone('');
    setFormRoleIds([]);
    setFormPassword('');
  };

  const openCreateModal = () => {
    resetForm();
    setEditingUser(null);
    setShowCreateModal(true);
  };

  const openEditModal = (user: ApiUser) => {
    setEditingUser(user);
    setFormFirstName(user.first_name);
    setFormLastName(user.last_name);
    setFormEmail(user.email);
    setFormPhone(user.phone || '');
    setFormRoleIds((user.role || []).map(r => r.id));
    setFormPassword('');
    setShowCreateModal(true);
  };

  const handleSubmit = () => {
    if (!formFirstName.trim() || !formLastName.trim() || !formEmail.trim()) {
      toast.error('Veuillez remplir tous les champs obligatoires.');
      return;
    }

    if (editingUser) {
      const payload: any = {
        first_name: formFirstName,
        last_name: formLastName,
        email: formEmail,
        phone: formPhone || undefined,
        role_ids: formRoleIds,
      };
      if (formPassword.trim()) {
        payload.password = formPassword;
      }
      dispatch(updateUserRequest(editingUser.id, payload, () => {
        setShowCreateModal(false);
        resetForm();
      }));
    } else {
      if (!formPassword.trim()) {
        toast.error('Veuillez définir un mot de passe.');
        return;
      }
      const payload = {
        first_name: formFirstName,
        last_name: formLastName,
        email: formEmail,
        phone: formPhone || undefined,
        password: formPassword,
        role_ids: formRoleIds,
      };
      dispatch(createUserRequest(payload, () => {
        setShowCreateModal(false);
        resetForm();
      }));
    }
  };

  const handleDelete = (id: string) => {
    dispatch(deleteUserRequest(id, () => {
      setShowDeleteConfirm(null);
    }));
  };

  const handleToggleActive = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    if (user.is_active) {
      // Suspend: POST /users/{id}/suspendre/
      dispatch(suspendUserRequest(userId, () => {
        setShowToggleConfirm(null);
      }));
    } else {
      // Activate: POST /users/{id}/activer/
      dispatch(activateUserRequest(userId, () => {
        setShowToggleConfirm(null);
      }));
    }
  };

  const toggleRoleId = (roleId: number) => {
    setFormRoleIds(prev =>
      prev.includes(roleId)
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const getInitials = (user: ApiUser) => {
    const f = user.first_name?.charAt(0) || '';
    const l = user.last_name?.charAt(0) || '';
    return (f + l).toUpperCase() || user.email.charAt(0).toUpperCase();
  };

  const getDisplayName = (user: ApiUser) => {
    if (user.first_name || user.last_name) {
      return `${user.first_name} ${user.last_name}`.trim();
    }
    return user.email;
  };

  // Unique role names from API roles for the filter dropdown
  const uniqueRoleNames = roles.map(r => r.name);

  if (isLoading && users.length === 0) {
    return (
      <div className="users-page">
        <div className="users-loading">
          <Loader size={32} className="users-spinner" />
          <span>Chargement des utilisateurs...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="users-page">
      {/* Stats */}
      <div className="users-stats">
        <div className="users-stat-card">
          <div className="users-stat-icon users-stat-icon-total">
            <UsersIcon size={20} />
          </div>
          <div className="users-stat-info">
            <span className="users-stat-label">TOTAL UTILISATEURS</span>
            <span className="users-stat-value">{totalUsers}</span>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-icon users-stat-icon-active">
            <UserCheck size={20} />
          </div>
          <div className="users-stat-info">
            <span className="users-stat-label">UTILISATEURS ACTIFS</span>
            <span className="users-stat-value">{activeUsers}</span>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-icon users-stat-icon-admin">
            <ShieldCheck size={20} />
          </div>
          <div className="users-stat-info">
            <span className="users-stat-label">ADMINISTRATEURS</span>
            <span className="users-stat-value">{adminCount}</span>
          </div>
        </div>
      </div>

      {/* Filters + actions */}
      <div className="users-toolbar">
        <div className="users-toolbar-left">
          <div className="users-search">
            <Search size={16} className="users-search-icon" />
            <input
              type="text"
              placeholder="Rechercher par nom ou email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="users-select"
          >
            <option value="Tous">Tous les rôles</option>
            {uniqueRoleNames.map(name => (
              <option key={name} value={name}>
                {roleColorMap[name]?.label || name}
              </option>
            ))}
          </select>
        </div>
        <button className="users-btn-create" onClick={openCreateModal}>
          <UserPlus size={16} /> Créer un utilisateur
        </button>
      </div>

      {/* Table */}
      <div className="users-table-wrap">
        <table className="users-table">
          <thead>
            <tr>
              <th>Utilisateur</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>Rôle</th>
              <th>Statut</th>
              <th>Date de création</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => {
              const rd = getRoleDisplay(u.role);
              return (
                <tr key={u.id}>
                  <td>
                    <div className="users-cell-user" style={{ cursor: 'pointer' }} onClick={() => navigate(`/espace-admin/utilisateurs/${u.id}`)}>
                      <div className="users-avatar">{getInitials(u)}</div>
                      <span className="users-username users-username-link">{getDisplayName(u)}</span>
                    </div>
                  </td>
                  <td className="users-email">{u.email}</td>
                  <td className="users-date">{u.phone || '—'}</td>
                  <td>
                    <span
                      className="users-role-badge"
                      style={{ color: rd.color, background: rd.bg }}
                    >
                      {(u.role || []).map(r => roleColorMap[r.name]?.label || r.name).join(', ') || 'Aucun'}
                    </span>
                  </td>
                  <td>
                    <div className="users-toggle-wrap">
                      <button
                        className={`users-toggle ${u.is_active ? 'users-toggle-on' : 'users-toggle-off'}`}
                        onClick={() => setShowToggleConfirm(u.id)}
                        title={u.is_active ? 'Désactiver' : 'Réactiver'}
                        disabled={isSubmitting}
                      >
                        <span className="users-toggle-knob" />
                      </button>
                      <span
                        className="users-toggle-label"
                        style={{ color: u.is_active ? '#059669' : '#dc2626' }}
                      >
                        {u.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                  </td>
                  <td className="users-date">
                    {u.created_at
                      ? new Date(u.created_at).toLocaleDateString('fr-FR')
                      : '—'
                    }
                  </td>
                  <td>
                    <div className="users-actions">
                      <button className="users-action-btn users-action-view" onClick={() => navigate(`/espace-admin/utilisateurs/${u.id}`)} title="Voir le détail">
                        <Eye size={14} />
                      </button>
                      <button className="users-action-btn users-action-edit" onClick={() => openEditModal(u)} title="Modifier">
                        <Edit2 size={14} />
                      </button>
                      <button className="users-action-btn users-action-delete" onClick={() => setShowDeleteConfirm(u.id)} title="Supprimer">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && !isLoading && (
          <div className="users-empty">Aucun utilisateur ne correspond aux critères.</div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="users-modal-overlay" onClick={() => !isSubmitting && setShowCreateModal(false)}>
          <div className="users-modal" onClick={e => e.stopPropagation()}>
            <div className="users-modal-head">
              <div className="users-modal-title-row">
                <div className="users-modal-icon">
                  <UserPlus size={20} />
                </div>
                <div>
                  <h3 className="users-modal-title">
                    {editingUser ? 'Modifier l\'utilisateur' : 'Créer un utilisateur'}
                  </h3>
                  <p className="users-modal-subtitle">
                    {editingUser ? 'Modifiez les informations du compte.' : 'Remplissez les informations du nouveau compte.'}
                  </p>
                </div>
              </div>
              <button className="users-modal-close" onClick={() => !isSubmitting && setShowCreateModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="users-modal-body">
              <div className="users-form-row">
                <div className="users-form-group">
                  <label>Prénom *</label>
                  <input
                    type="text"
                    placeholder="ex: Jean"
                    value={formFirstName}
                    onChange={(e) => setFormFirstName(e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="users-form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    placeholder="ex: Kouassi"
                    value={formLastName}
                    onChange={(e) => setFormLastName(e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <div className="users-form-group">
                <label>Email *</label>
                <input
                  type="email"
                  placeholder="ex: j.kouassi@tca.ci"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div className="users-form-group">
                <label>Téléphone</label>
                <input
                  type="tel"
                  placeholder="ex: +225 07 00 00 00"
                  value={formPhone}
                  onChange={(e) => setFormPhone(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div className="users-form-group">
                <label>Rôles *</label>
                <div className="users-roles-checkboxes">
                  {rolesLoading ? (
                    <span className="users-roles-loading">Chargement...</span>
                  ) : roles.length > 0 ? (
                    roles.map(role => (
                      <label key={role.id} className="users-role-checkbox">
                        <input
                          type="checkbox"
                          checked={formRoleIds.includes(role.id)}
                          onChange={() => toggleRoleId(role.id)}
                          disabled={isSubmitting}
                        />
                        <span
                          className="users-role-checkbox-label"
                          style={{
                            color: roleColorMap[role.name]?.color || '#333',
                            background: roleColorMap[role.name]?.bg || '#f3f4f6',
                          }}
                        >
                          {roleColorMap[role.name]?.label || role.name}
                        </span>
                      </label>
                    ))
                  ) : (
                    <span className="users-roles-loading">Aucun rôle disponible</span>
                  )}
                </div>
              </div>
              {(!editingUser || formPassword) && (
                <div className="users-form-group">
                  <label>{editingUser ? 'Nouveau mot de passe' : 'Mot de passe *'}</label>
                  <input
                    type="password"
                    placeholder={editingUser ? 'Laisser vide pour ne pas changer' : 'Mot de passe temporaire'}
                    value={formPassword}
                    onChange={(e) => setFormPassword(e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              )}
              {editingUser && !formPassword && (
                <div className="users-form-group">
                  <button
                    type="button"
                    className="users-btn-change-pwd"
                    onClick={() => setFormPassword(' ')}
                  >
                    Changer le mot de passe
                  </button>
                </div>
              )}
            </div>

            <div className="users-modal-foot">
              <button className="users-btn-cancel" onClick={() => !isSubmitting && setShowCreateModal(false)} disabled={isSubmitting}>
                Annuler
              </button>
              <button className="users-btn-submit" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'En cours...' : editingUser ? 'Enregistrer' : 'Créer le compte'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="users-modal-overlay" onClick={() => !isSubmitting && setShowDeleteConfirm(null)}>
          <div className="users-modal users-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="users-modal-head">
              <div className="users-modal-title-row">
                <div className="users-modal-icon users-modal-icon-danger">
                  <Trash2 size={20} />
                </div>
                <div>
                  <h3 className="users-modal-title">Supprimer l'utilisateur</h3>
                  <p className="users-modal-subtitle">
                    Êtes-vous sûr de vouloir supprimer « {getDisplayName(users.find(u => u.id === showDeleteConfirm)!)} » ? Cette action est irréversible.
                  </p>
                </div>
              </div>
              <button className="users-modal-close" onClick={() => !isSubmitting && setShowDeleteConfirm(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="users-modal-foot">
              <button className="users-btn-cancel" onClick={() => !isSubmitting && setShowDeleteConfirm(null)} disabled={isSubmitting}>
                Annuler
              </button>
              <button className="users-btn-danger" onClick={() => handleDelete(showDeleteConfirm)} disabled={isSubmitting}>
                {isSubmitting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle statut confirmation modal */}
      {showToggleConfirm && (() => {
        const targetUser = users.find(u => u.id === showToggleConfirm);
        if (!targetUser) return null;
        const isActivating = !targetUser.is_active;
        return (
          <div className="users-modal-overlay" onClick={() => !isSubmitting && setShowToggleConfirm(null)}>
            <div className="users-modal users-modal-sm" onClick={e => e.stopPropagation()}>
              <div className="users-modal-head">
                <div className="users-modal-title-row">
                  <div className={`users-modal-icon ${isActivating ? '' : 'users-modal-icon-danger'}`}>
                    <ShieldOff size={20} />
                  </div>
                  <div>
                    <h3 className="users-modal-title">
                      {isActivating ? 'Réactiver le compte' : 'Désactiver le compte'}
                    </h3>
                    <p className="users-modal-subtitle">
                      {isActivating
                        ? `Voulez-vous réactiver le compte de « ${getDisplayName(targetUser)} » ? L'utilisateur pourra se connecter à nouveau.`
                        : `Voulez-vous désactiver le compte de « ${getDisplayName(targetUser)} » ? L'utilisateur ne pourra plus se connecter.`
                      }
                    </p>
                  </div>
                </div>
                <button className="users-modal-close" onClick={() => !isSubmitting && setShowToggleConfirm(null)}>
                  <X size={18} />
                </button>
              </div>
              <div className="users-modal-foot">
                <button className="users-btn-cancel" onClick={() => !isSubmitting && setShowToggleConfirm(null)} disabled={isSubmitting}>
                  Annuler
                </button>
                <button
                  className={isActivating ? 'users-btn-submit' : 'users-btn-danger'}
                  onClick={() => handleToggleActive(showToggleConfirm)}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'En cours...' : isActivating ? 'Réactiver' : 'Désactiver'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Users;
